import camelcaseKeys from 'camelcase-keys';
import { jwtDecode } from 'jwt-decode';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { Loader } from '@app-evolution-cms/element-ui';

import { exchangeTokens } from '../../api/auth';
import { AuthTokensResponse } from '../../types/api/auth';
import {
  AUTH_BROADCASTER_ACTIONS,
  authBroadcaster,
  getTokens,
  handleLogin,
  handleLogout,
  isValidToken,
  notifyLogout,
  removeTokens,
  saveTokens,
} from '../../utils/auth';
import { createContext } from '../../utils/context';
import { AuthProviderValue, UserIdentity } from './types';

const [Provider, useProviderValue] = createContext<AuthProviderValue>();

export function AuthProvider({ children }: PropsWithChildren) {
  const userIdentity = useRef<UserIdentity>(undefined);
  const [authReady, setAuthReady] = useState(false);

  const saveSession = (userTokens: AuthTokensResponse) => {
    saveTokens(userTokens);

    setAuthReady(true);
  };

  const decodeUserIdentity = (idToken: string) => {
    const identity = camelcaseKeys(jwtDecode(idToken), {
      deep: true,
    }) as UserIdentity;
    userIdentity.current = identity;
  };

  const checkSession = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlAuthCode = urlParams.get('code') ?? undefined;

    const urlAccessToken = urlParams.get('access_token') ?? undefined;
    const urlIdToken = urlParams.get('id_token') ?? undefined;
    const urlRefreshToken = urlParams.get('refresh_token') ?? undefined;

    if (urlAuthCode || urlAccessToken || urlIdToken || urlRefreshToken) {
      // Clear tokens BEFORE exchanging auth params to prevent conflicts with corrupted/expired tokens
      removeTokens();

      // Clear the URL immediately to prevent caching issues.
      window.history.replaceState({}, '', window.location.pathname);

      try {
        const authResponse = await exchangeTokens({
          code: urlAuthCode,
          accessToken: urlAccessToken,
          idToken: urlIdToken,
          refreshToken: urlRefreshToken,
        });

        saveSession(authResponse);
        decodeUserIdentity(authResponse.idToken);
      } catch {
        // If the exchange fails, redirect to login
        handleLogin();
      }
      return;
    }

    const authTokens = getTokens();

    if (!authTokens.accessToken || !isValidToken(authTokens.accessToken)) {
      handleLogin();
      return;
    }

    decodeUserIdentity(authTokens.idToken);
    setAuthReady(true);
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    // Cross tab communication
    authBroadcaster.onmessage = (message) => {
      if (message.data.action === AUTH_BROADCASTER_ACTIONS.logout) {
        handleLogout();
      }
    };
  }, []);

  if (!authReady || !userIdentity.current) {
    return (
      <div className="absolute flex size-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <Provider
      value={{
        logout: () => {
          notifyLogout();
          handleLogout();
        },
        userIdentity: userIdentity.current,
      }}
    >
      {children}
    </Provider>
  );
}

export const useAuth = useProviderValue;
